/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SelectOutboundRoute.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Reusable component for selecting an outbound route with search functionality.
    Used in ARS for selecting routes with priority.

 🔧 Notes:
    - Searchable select component
    - Single selection
    - Filters by route name and description

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface OutboundRoute {
  id: number;
  name: string;
  description?: string;
}

interface SelectOutboundRouteProps {
  value: number | null;
  onChange: (value: number | null) => void;
  routes: OutboundRoute[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

export default function SelectOutboundRoute({
  value,
  onChange,
  routes,
  placeholder = 'Select Outbound Route',
  disabled = false,
  className = '',
  error = false
}: SelectOutboundRouteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get selected route
  const selectedRoute = value ? routes.find(r => r.id === value) : null;

  // Filter routes based on search
  const filteredRoutes = routes.filter(route =>
    route.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (route.description && route.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (routeId: number | null) => {
    onChange(routeId);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-md text-sm
          bg-background text-foreground
          ${error ? 'border-red-500' : 'border-input'}
          hover:bg-accent hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between
          transition-colors
        `}
      >
        <span className={selectedRoute ? '' : 'text-muted-foreground'}>
          {selectedRoute ? selectedRoute.name : placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search routes..."
              className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto p-1">
            {/* Clear selection option */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={`
                w-full px-3 py-2 text-left rounded-md text-sm
                hover:bg-accent hover:text-accent-foreground
                flex items-center justify-between
                transition-colors
                ${!value ? 'bg-accent text-accent-foreground' : ''}
              `}
            >
              <span className="text-muted-foreground italic">None</span>
              {!value && <Check className="h-4 w-4 text-primary" />}
            </button>

            {filteredRoutes.length > 0 ? (
              filteredRoutes.map((route) => (
                <button
                  key={route.id}
                  type="button"
                  onClick={() => handleSelect(route.id)}
                  className={`
                    w-full px-3 py-2 text-left rounded-md text-sm
                    hover:bg-accent hover:text-accent-foreground
                    flex items-center justify-between
                    transition-colors
                    ${value === route.id ? 'bg-accent text-accent-foreground' : ''}
                  `}
                >
                  <div className="flex-1">
                    <div className="font-medium">{route.name}</div>
                    {route.description && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {route.description}
                      </div>
                    )}
                  </div>
                  {value === route.id && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No routes found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

